import type { CompanyGraph, GraphCluster, GraphEdge } from "@/lib/company-graph";

// A GRAPH WITH ITS GROUPS FOLDED IN, which is an ordinary graph.
//
// This is the whole trick of the cluster layer: the three layouts never learn what a cluster is.
// They place whatever graph they are handed, and this hands them one whose nodes are the groups,
// the ungrouped things, and the members of whichever groups are open. Opening one is the same
// function with one more id in the set.
//
// NOTHING IS LOST, because the collapse happens here and not in the file: every fine node and
// every edge is still in the graph the server sent, and the picture is a view of it.

/** The member edges one drawn edge stands for — so a walk over a leaf still lights its group. */
export interface Rolled {
  company: string;
  predicate: string;
  object: string;
}

export interface Collapsed {
  graph: CompanyGraph;
  /** Which drawn nodes are groups, so the view can mark them and know what opens. */
  clusters: ReadonlySet<string>;
  /** Edge key → the member edges it rolled up. Empty for an edge that is already a leaf's. */
  rolled: ReadonlyMap<string, Rolled[]>;
  /**
   * For each opened group, the ids DRAWN inside it — which are its members only while none of
   * them is open in turn. The box round an opened group is drawn from this: its members are not
   * enough, because a member that is itself open is not on the picture any more.
   */
  inside: ReadonlyMap<string, string[]>;
  /**
   * The other way round: for each drawn node, the opened groups around it, OUTERMOST FIRST.
   *
   * What the layout needs and cannot work out. Ordering a node by its innermost group alone splits
   * an outer group's contents into two blocks, and the box drawn round them then spans everything
   * in between — which is what a second open did to the picture.
   */
  enclosing: ReadonlyMap<string, string[]>;
  /** How many levels this graph holds, so the view knows whether `+` has to ask the server. */
  levels: number;
}

/**
 * The graph as a VIEW of a few companies and things, or the whole thing when none are chosen.
 *
 * A FILTER ON THE PICTURE AND NOTHING ELSE. The graph that answers questions is untouched — this
 * is the same reshaping `collapse` does, one step earlier: a reader looking at twenty companies
 * out of a hundred and fifty wants the other hundred and thirty gone from the drawing, not from
 * the corpus.
 *
 * A thing no chosen company reaches goes with them. Left in, it is a name with no line attached,
 * and a picture of those is a worse answer to "what do these twenty do" than no picture at all —
 * so the layout reflows over what is left rather than keeping holes where the rest used to be.
 */
/**
 * The groups this graph really has, read by the same rules the search reads them by.
 *
 * Exported so the filter OFFERS exactly what the drawing draws: a group the loader would refuse
 * must not appear in a list of things to pick, or picking it selects nothing and reads as broken.
 */
export function groupsIn(graph: CompanyGraph): GraphCluster[] {
  return merged(graph.clusters ?? [], graph.nodes.map((it) => it.label));
}

/**
 * The node labels a set of chosen names stands for: a thing is itself, a group is its leaves.
 *
 * A GROUP IS NEVER AN EDGE'S OBJECT. It is a handle on the things under it, so picking one has to
 * mean picking those — through as many levels as there are, exactly as a question naming a group
 * does. Taken literally it would match no edge at all and the filter would look broken.
 */
export function leavesOf(graph: CompanyGraph, chosen: ReadonlySet<string>): Set<string> {
  const groups = new Map(groupsIn(graph).map((it) => [it.label, it]));
  const out = new Set<string>();
  const walk = (name: string, depth: number) => {
    const group = groups.get(name);
    if (!group || depth > 16) {
      out.add(name);
      return;
    }
    for (const member of group.members) walk(member, depth + 1);
  };
  for (const name of chosen) walk(name, 0);
  return out;
}

export function narrow(
  graph: CompanyGraph,
  companies: ReadonlySet<string>,
  things: ReadonlySet<string> = new Set(),
  relations: ReadonlySet<string> = new Set(),
  sources: ReadonlySet<string> = new Set(),
): CompanyGraph {
  if (companies.size === 0 && things.size === 0 && relations.size === 0 && sources.size === 0) return graph;
  const picked = leavesOf(graph, things);
  // THE SOURCE NARROWS BY THE THING, not by the edge, because that is where it is recorded and
  // where it is drawn: the shape on the picture is the node's. An edge is kept when the thing it
  // points at is, so "labels only" is the picture of what the companies listed and nothing else.
  const sourced = sources.size === 0
    ? null
    : new Set(graph.nodes.filter((it) => sources.has(it.source ?? "summary")).map((it) => it.label));
  // THE RELATION NARROWS LIKE THE OTHER TWO, which is the whole point of it being here rather than
  // a dimmer on the drawing: asking for SUPPLIES means a picture of who supplies whom, not a
  // hundred and fifty companies standing around six lit lines.
  const edges = graph.edges.filter(
    (it) =>
      (companies.size === 0 || companies.has(it.company)) &&
      (things.size === 0 || picked.has(it.object)) &&
      (relations.size === 0 || relations.has(it.predicate)) &&
      (sourced === null || sourced.has(it.object)),
  );
  const reachedThings = new Set(edges.map((it) => it.object));
  const reachedCompanies = new Set(edges.map((it) => it.company));
  // CHOSEN MEANS SHOWN, EVEN WITH NOTHING ON IT. A company a reader picked that turns out to reach
  // none of the things they also picked is an answer — "these two have nothing between them" —
  // and dropping it would make the picture agree with whatever they expected.
  const kept = companies.size > 0
    ? graph.companies.filter((it) => companies.has(it))
    : graph.companies.filter((it) => reachedCompanies.has(it));
  const standing = new Set(kept);
  return {
    ...graph,
    companies: kept,
    // `to` is a company too: a supply arrow to someone who is not on the picture has nowhere to
    // go, so the edge keeps its commodity and loses its customer rather than pointing off it.
    edges: edges.map((it) => (it.to && !standing.has(it.to) ? { ...it, to: undefined } : it)),
    nodes: (things.size > 0
      ? graph.nodes.filter((it) => picked.has(it.label))
      : graph.nodes.filter((it) => reachedThings.has(it.label))
    ).filter((it) => sourced === null || sourced.has(it.label)),
    // Members that are gone are dropped by `collapse`, which already reads the clusters by the
    // loader's rules — a group left with nothing is not a group.
    clusters: graph.clusters,
  };
}

const key = (company: string, predicate: string, object: string) => `${company}\u0000${predicate}\u0000${object}`;

/**
 * The groups this graph really has, read by the SAME RULES the service reads them by.
 *
 * The page is served the raw file (`/agent/company-graph` strips only `embedding`), so every rule
 * `CompanyGraph.load` enforces on the clusters block applies to the search and not, until here, to
 * the picture. Left apart they disagree about what the graph holds: a node claimed by two groups
 * is drawn in the second and searched in the first, and a group named after a node is drawn as one
 * mark that is both while the search refuses it outright.
 *
 * The rules, in the loader's order: one label is one group; a name the graph already uses is not a
 * name; a member belongs to the first group that claims it; a group with nothing left is not a
 * group; and a level is checked against the one beneath it.
 */
function merged(clusters: readonly GraphCluster[], nodes: readonly string[]): GraphCluster[] {
  const by = new Map<string, GraphCluster>();
  for (const cluster of clusters) {
    const id = `${cluster.level ?? 1}\u0000${cluster.label.toLowerCase()}`;
    const already = by.get(id);
    by.set(id, {
      label: already?.label ?? cluster.label,
      level: cluster.level ?? 1,
      members: [...new Set([...(already?.members ?? []), ...cluster.members])],
    });
  }

  const taken = new Set(nodes.map((it) => it.toLowerCase()));
  const kept: GraphCluster[] = [];
  let beneath = new Set(nodes);
  const byLevel = [...by.values()].reduce((levels, it) => {
    levels.set(it.level ?? 1, [...(levels.get(it.level ?? 1) ?? []), it]);
    return levels;
  }, new Map<number, GraphCluster[]>());

  // Ascending, because each layer is checked against the one beneath it — and a layer that loses
  // everything takes the layers above it with it.
  for (const level of [...byLevel.keys()].sort((a, b) => a - b)) {
    const claimed = new Set<string>();
    const here: GraphCluster[] = [];
    for (const cluster of byLevel.get(level)!) {
      if (taken.has(cluster.label.toLowerCase())) continue;
      const members = cluster.members.filter((it) => beneath.has(it) && !claimed.has(it));
      if (members.length === 0) continue;
      for (const member of members) claimed.add(member);
      taken.add(cluster.label.toLowerCase());
      here.push({ ...cluster, members, level });
    }
    kept.push(...here);
    beneath = new Set(here.map((it) => it.label));
  }
  return kept;
}

/**
 * Folds every closed group into one node, and the edges into it.
 *
 * [level] is how high to fold: 0 draws the bare nodes, 1 their groups, 2 the groups of those. A
 * node is drawn as the HIGHEST group over it that is still folded — so opening a level-2 group
 * reveals the level-1 groups inside it rather than jumping to the leaves, and `−` is a step back
 * up the same ladder `+` climbed.
 *
 * GROUPED BY `(company, predicate, cluster)` AND NOT BY `(company, cluster)`. A company that offers
 * one member and consumes another is doing two different things, and merging them into one line
 * would say it does neither.
 */
export function collapse(
  graph: CompanyGraph,
  opened: ReadonlySet<string> = new Set(),
  level = Number.POSITIVE_INFINITY,
): Collapsed {
  const all = merged(graph.clusters ?? [], graph.nodes.map((it) => it.label));
  const levels = all.reduce((most, it) => Math.max(most, it.level ?? 1), 0);
  const show = Math.min(level, levels);
  if (show < 1) {
    return { graph, clusters: new Set(), rolled: new Map(), inside: new Map(), enclosing: new Map(), levels };
  }

  const parent = new Map<string, GraphCluster>();
  for (const cluster of all) {
    if ((cluster.level ?? 1) > show) continue;
    for (const member of cluster.members) parent.set(member, cluster);
  }

  // Each node's ancestors, lowest first. Walked once here rather than per lookup, and capped so a
  // graph that somehow names a group its own ancestor costs a short list instead of the tab.
  const chain = new Map<string, string[]>();
  for (const node of graph.nodes) {
    const up: string[] = [];
    let at: string = node.label;
    for (let owner = parent.get(at); owner && up.length < 16; owner = parent.get(at)) {
      up.push(owner.label);
      at = owner.label;
    }
    chain.set(node.label, up);
  }

  /** What is drawn in a node's place: the first folded group from the top, else the node itself. */
  const drawnFor = (id: string): string => {
    const up = chain.get(id) ?? [];
    for (let i = up.length - 1; i >= 0; i -= 1) if (!opened.has(up[i]!)) return up[i]!;
    return id;
  };

  const seen = new Set<string>();
  const nodes = [];
  for (const node of graph.nodes) {
    const stands = drawnFor(node.label);
    if (seen.has(stands)) continue;
    seen.add(stands);
    nodes.push(stands === node.label ? node : { label: stands });
  }
  const named = new Set(all.map((it) => it.label));

  const edges = new Map<string, GraphEdge & { weight: number }>();
  const rolled = new Map<string, Rolled[]>();
  for (const edge of graph.edges) {
    const stands = drawnFor(edge.object);
    if (stands === edge.object) {
      edges.set(`leaf:${edges.size}`, { ...edge, weight: 1 });
      continue;
    }
    const id = key(edge.company, edge.predicate, stands);
    const already = edges.get(id);
    if (already) {
      // WEIGHT IS HOW MANY MEMBERS IT REACHES, which is the one thing the drawn edge can say that
      // the group's name cannot: a company that touches four of a group's things has a different
      // relationship to it than one that touches one.
      already.weight += 1;
    } else {
      // `to` and `evidence` are dropped: both belong to a particular member edge, and carrying one
      // of several would claim the group was supplied to that customer with that evidence.
      edges.set(id, {
        company: edge.company,
        predicate: edge.predicate,
        object: stands,
        status: edge.status,
        weight: 1,
      });
    }
    rolled.set(id, [...(rolled.get(id) ?? []), { company: edge.company, predicate: edge.predicate, object: edge.object }]);
  }

  // Only the OPENED RUN from the top of a chain is on the picture. An opened group below a folded
  // one is inside something folded — it has no box, because it has no place.
  const inside = new Map<string, Set<string>>();
  const enclosing = new Map<string, string[]>();
  for (const [node, up] of chain) {
    const stands = drawnFor(node);
    const around: string[] = [];
    for (let i = up.length - 1; i >= 0; i -= 1) {
      if (!opened.has(up[i]!)) break;
      around.push(up[i]!);
      const within = inside.get(up[i]!) ?? new Set<string>();
      within.add(stands);
      inside.set(up[i]!, within);
    }
    // Every node standing for the same drawn id has the same opened groups above that id, so the
    // first one to arrive settles it.
    if (around.length > 0 && !enclosing.has(stands)) enclosing.set(stands, around);
  }

  // OPENING A GROUP HAPPENS IN PLACE. The members take their own positions in the picture they
  // belong to, rather than replacing it with a view of themselves: a reader opening a group is
  // asking what is inside it, not asking to leave the graph.
  return {
    graph: { ...graph, nodes, edges: [...edges.values()] },
    clusters: new Set(nodes.map((it) => it.label).filter((it) => named.has(it))),
    rolled,
    inside: new Map([...inside].map(([label, within]) => [label, [...within]])),
    enclosing,
    levels,
  };
}

export const collapsedKey = key;
